import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, FileDown, TrendingUp, TrendingDown, Minus, Download, Trash2 } from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RTooltip,
    ResponsiveContainer,
    ReferenceLine,
} from "recharts";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import AddMeasurementModal, { TYPES } from "../components/health/AddMeasurementModal";
import ShareWithDoctorButton from "../components/emergency/ShareWithDoctorButton";
import { api } from "../api/client";
import { useFamily } from "../contexts/FamilyContext";

const PERIODS = [
    { value: "7d", label: "7d" },
    { value: "30d", label: "30d" },
    { value: "90d", label: "90d" },
    { value: "365d", label: "1y" },
];

const TRACKED_TYPES = ["blood_pressure", "blood_sugar", "weight", "pulse", "spo2", "hba1c"];

const formatVal = (v, type) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "object" && v.systolic !== undefined) {
        return `${Math.round(v.systolic)}/${Math.round(v.diastolic)}`;
    }
    if (typeof v === "number") return v.toFixed(v < 10 ? 2 : 1).replace(/\.0+$/, "");
    return String(v);
};

const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const TrendIcon = ({ pct }) => {
    if (pct == null) return <Minus className="h-3.5 w-3.5 text-zinc-400" />;
    if (pct > 1) return <TrendingUp className="h-3.5 w-3.5 text-rose-500" />;
    if (pct < -1) return <TrendingDown className="h-3.5 w-3.5 text-emerald-500" />;
    return <Minus className="h-3.5 w-3.5 text-zinc-400" />;
};

const SummaryCard = ({ type, summary, onAddClick }) => {
    const t = TYPES.find((x) => x.value === type) || { label: type, unit: "" };
    const Icon = t.Icon || TrendingUp;
    const series = (summary?.series || []).map((s) => ({
        at: s.taken_at,
        label: new Date(s.taken_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" }),
        v: typeof s.value === "object" && s.value?.systolic !== undefined ? s.value.systolic : s.value,
        v2: typeof s.value === "object" && s.value?.diastolic !== undefined ? s.value.diastolic : null,
    }));
    const isBP = type === "blood_pressure";

    return (
        <div
            data-testid={`health-summary-${type}`}
            className="overflow-hidden rounded-3xl border border-zinc-100 bg-white p-5 shadow-soft dark:border-white/10 dark:bg-white/[0.03]"
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <span className="grid h-9 w-9 place-items-center rounded-2xl bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                        <Icon className="h-4 w-4" />
                    </span>
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{t.label}</p>
                        <p className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">
                            {summary?.latest ? formatVal(summary.latest.value, type) : "—"}
                            <span className="ml-1 text-xs font-normal text-zinc-500 dark:text-zinc-400">{t.unit}</span>
                        </p>
                    </div>
                </div>
                <button onClick={() => onAddClick(type)} className="rounded-full bg-brand-50 p-1.5 text-brand-700 hover:bg-brand-100 dark:bg-brand-500/15 dark:text-brand-300 dark:hover:bg-brand-500/25" data-testid={`health-add-${type}`} aria-label={`Add ${t.label}`}>
                    <Plus className="h-3.5 w-3.5" />
                </button>
            </div>

            <div className="mt-3 flex items-center gap-3 text-[11px] text-zinc-600 dark:text-zinc-400">
                <span className="inline-flex items-center gap-0.5">
                    <TrendIcon pct={summary?.trend_pct} />
                    {summary?.trend_pct != null ? `${summary.trend_pct > 0 ? "+" : ""}${summary.trend_pct}%` : "—"}
                </span>
                <span>· avg {isBP ? (summary?.avg?.systolic ? `${Math.round(summary.avg.systolic)}/${Math.round(summary.avg.diastolic)}` : "—") : formatVal(summary?.avg, type)}</span>
                <span>· n={summary?.count ?? 0}</span>
            </div>

            <div className="mt-3 h-24">
                {series.length === 0 ? (
                    <div className="grid h-full place-items-center rounded-xl border border-dashed border-zinc-200 text-[11px] text-zinc-400 dark:border-white/10 dark:text-zinc-500">
                        No data — log your first reading
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={series} margin={{ top: 5, right: 5, bottom: 0, left: -25 }}>
                            <CartesianGrid stroke="rgba(124,58,237,0.08)" strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 9, fill: "currentColor" }} className="text-zinc-400" stroke="rgba(0,0,0,0.1)" />
                            <YAxis tick={{ fontSize: 9, fill: "currentColor" }} className="text-zinc-400" stroke="rgba(0,0,0,0.1)" domain={["auto", "auto"]} />
                            <RTooltip
                                contentStyle={{ background: "rgba(15,15,20,0.9)", borderRadius: 8, border: "none", color: "#fff", fontSize: 11 }}
                                labelStyle={{ color: "#A78BFA" }}
                            />
                            <Line type="monotone" dataKey="v" stroke="#7c3aed" strokeWidth={2} dot={{ r: 1.5 }} animationDuration={300} />
                            {isBP && <Line type="monotone" dataKey="v2" stroke="#f97316" strokeWidth={2} dot={{ r: 1.5 }} animationDuration={300} />}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

const HealthContent = () => {
    const { activeMember, activeFamilyMemberQuery } = useFamily();
    const [period, setPeriod] = useState("30d");
    const [summaries, setSummaries] = useState({});
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [addingType, setAddingType] = useState("blood_pressure");
    const [downloading, setDownloading] = useState(false);

    const fmIdParam = useMemo(() => {
        if (!activeMember) return null;
        if (activeMember.is_self) return null;
        return activeMember.id;
    }, [activeMember]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = (extra) => {
                const p = { ...extra };
                if (fmIdParam) p.family_member_id = fmIdParam;
                return p;
            };
            const summariesData = await Promise.all(
                TRACKED_TYPES.map((type) =>
                    api.get("/health/measurements/summary", { params: params({ type, period }) })
                        .then(({ data }) => [type, data])
                        .catch(() => [type, null])
                )
            );
            const sMap = {};
            summariesData.forEach(([t, d]) => { sMap[t] = d; });
            setSummaries(sMap);
            const { data: recentList } = await api.get("/health/measurements", { params: params({ page_size: 20 }) });
            setRecent(recentList?.items || []);
        } finally {
            setLoading(false);
        }
    }, [period, fmIdParam]);

    useEffect(() => { load(); }, [load]);

    const onSaved = () => { load(); };

    const onDeleteMeasurement = async (id) => {
        if (!window.confirm("Delete this measurement?")) return;
        try {
            await api.delete(`/health/measurements/${id}`);
            toast.success("Deleted");
            load();
        } catch { toast.error("Delete failed"); }
    };

    const onDownload = async (format) => {
        try {
            setDownloading(true);
            const params = { period, format };
            if (fmIdParam) params.family_member_id = fmIdParam;
            const res = await api.get("/health/report", { params, responseType: "blob" });
            const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `pills-health-${period}.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`${format.toUpperCase()} downloaded`);
        } catch {
            toast.error("Could not generate report");
        } finally {
            setDownloading(false);
        }
    };

    const openAdd = (type = "blood_pressure") => { setAddingType(type); setAdding(true); };

    return (
        <div data-testid="health-page">
            <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="label-uppercase">Health tracker</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
                        {activeMember?.is_self ? "Your health, at a glance" : `${activeMember?.full_name || "Profile"} · health overview`}
                    </h1>
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                        Log readings, spot trends, share a doctor-ready report in one tap.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-full border border-zinc-200 bg-white p-1 dark:border-white/10 dark:bg-white/[0.03]" data-testid="health-period-toggle">
                        {PERIODS.map((p) => (
                            <button
                                key={p.value}
                                onClick={() => setPeriod(p.value)}
                                data-testid={`health-period-${p.value}`}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition ${period === p.value ? "bg-brand-700 text-white" : "text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-white/5"}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => openAdd()} className="btn-brand !py-2 !px-4 !text-sm" data-testid="health-add-btn">
                        <Plus className="h-4 w-4" /> Log reading
                    </button>
                    <div className="relative inline-flex">
                        <button
                            onClick={() => onDownload("pdf")}
                            disabled={downloading}
                            data-testid="health-download-pdf"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-3 py-2 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:border-brand-400/30 dark:bg-transparent dark:text-brand-300 dark:hover:bg-brand-500/10"
                        >
                            <FileDown className="h-3.5 w-3.5" /> {downloading ? "Generating..." : "PDF report"}
                        </button>
                        <button
                            onClick={() => onDownload("csv")}
                            disabled={downloading}
                            data-testid="health-download-csv"
                            className="ml-1 inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 dark:border-white/10 dark:bg-transparent dark:text-zinc-300 dark:hover:bg-white/5"
                        >
                            <Download className="h-3.5 w-3.5" /> CSV
                        </button>
                    </div>
                    <ShareWithDoctorButton testId="health-share-doctor" />
                </div>
            </header>

            {loading ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-48 skeleton rounded-3xl" />)}
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="health-summary-grid">
                    {TRACKED_TYPES.map((t) => (
                        <SummaryCard key={t} type={t} summary={summaries[t]} onAddClick={openAdd} />
                    ))}
                </div>
            )}

            <div className="mt-10">
                <div className="mb-3 flex items-end justify-between">
                    <div>
                        <p className="label-uppercase">Recent log</p>
                        <h2 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Last readings</h2>
                    </div>
                </div>
                {recent.length === 0 ? (
                    <EmptyState
                        title="No measurements yet"
                        description="Start by logging your first reading — pick any vital from the buttons above."
                        action={<button onClick={() => openAdd()} className="btn-brand">Log first reading</button>}
                    />
                ) : (
                    <div className="overflow-hidden rounded-3xl border border-zinc-100 bg-white shadow-soft dark:border-white/10 dark:bg-white/[0.03]">
                        <table className="w-full text-sm" data-testid="health-recent-table">
                            <thead className="bg-zinc-50/60 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:bg-white/[0.02] dark:text-zinc-400">
                                <tr>
                                    <th className="px-4 py-3">When</th>
                                    <th className="px-4 py-3">Type</th>
                                    <th className="px-4 py-3">Value</th>
                                    <th className="px-4 py-3">Context</th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {recent.map((r) => (
                                    <tr key={r.id} className="border-t border-zinc-100 dark:border-white/5">
                                        <td className="px-4 py-3 text-xs text-zinc-600 dark:text-zinc-300">{formatDate(r.taken_at)}</td>
                                        <td className="px-4 py-3 text-zinc-900 dark:text-zinc-100">
                                            {(TYPES.find((t) => t.value === r.type) || {}).label || r.type}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-50">
                                            {formatVal(r.value, r.type)} <span className="text-xs text-zinc-500 dark:text-zinc-400">{r.unit || ""}</span>
                                            {r.bp_category && (
                                                <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                                    r.bp_category === "normal" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                                                        : r.bp_category === "elevated" ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                                                        : "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300"
                                                }`}>{r.bp_category.replace("_", " ")}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-zinc-600 dark:text-zinc-400">{r.meal_context || "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => onDeleteMeasurement(r.id)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-50 hover:text-rose-500 dark:hover:bg-white/5" aria-label="Delete" data-testid={`health-delete-${r.id}`}>
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <AddMeasurementModal open={adding} onClose={() => setAdding(false)} defaultType={addingType} onSaved={onSaved} />
        </div>
    );
};

const Health = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <HealthContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Health;
